import { useCallback, useEffect, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { TaskExecution } from '../types/task';
import { tasksApi } from '../services/api/tasks';
import { queryKeys } from '../lib/queryKeys';
import { useOptionalWebSocket } from '../contexts/WebSocketContext';

/**
 * THUB-187: cursor-paginated Task History.
 *
 * The Task History tab must reach every execution row, not just the first slice the container
 * happened to fetch. Post-THUB-204 hosted-inbound writes one row PER FILE, so a busy task blows
 * past any fixed initial slice immediately — the container prop can't feed all pages.
 *
 * The cursor is OPAQUE (lane2 #227): we pass nextCursor back verbatim and never parse or
 * construct it. nextCursor === null means end of history.
 *
 * THUB-299: this was a hand-rolled fetcher — four useState values plus three refs
 * (latestTaskId / loading / cursor) doing race-guarding, cursor threading and page accumulation
 * by hand. It is now useInfiniteQuery, which owns all of that.
 *
 * The reason it mattered is not tidiness. Observed on DEV 2026-08-12: the History tab sat at
 * 03:38 while the clock read 03:42. Executions were being written the whole time; the old hook
 * fetched once on activation with no subscription and no refetch, so a tab left open went
 * silently stale — and because its rows lived in local useState they were invisible to the query
 * cache, so nothing could refresh them.
 *
 * How that staleness is prevented CHANGED on 2026-08-25, deliberately. It used to sit under the
 * ['tasks'] prefix so the central execution_updates flush refetched it. That worked, but TanStack
 * refetches an infinite query's pages as a SET, so the cost scaled with how far the operator had
 * scrolled rather than with the data -- NP's largest task (~10,968 executions) reached ~470 req/s
 * and ~27 MB/s from a single tab, unbounded in pages.
 *
 * It is now keyed outside that prefix, and freshness is explicit instead: this hook subscribes to
 * `execution_updates` for its OWN task and raises `hasPendingUpdates`, which the History tab renders
 * as a "New executions available - Refresh" cue. The tab still cannot silently go stale -- the
 * operator is told -- but the page walk happens when they ask for it. Live watching is the Monitor
 * tab's job.
 */

const PAGE_SIZE = 50; // contract max; server clamps anything larger

interface ExecutionPage {
  executions: TaskExecution[];
  nextCursor: string | null;
}

export interface UseTaskExecutionHistoryReturn {
  /** Accumulated execution rows across all loaded pages (newest-first as returned). */
  executions: TaskExecution[];
  /** True while any page (first or subsequent) is in flight. */
  isLoading: boolean;
  /** A background refetch is in flight; rows are still valid and Load More stays usable. */
  isRefreshing: boolean;
  /** New executions have been broadcast for this task since the rows were last read. */
  hasPendingUpdates: boolean;
  /** Error message for the most recent failed load, else null. */
  error: string | null;
  /** Whether another page is available (nextCursor is non-null). */
  hasMore: boolean;
  /** Load the next page and append it. No-op while loading or when exhausted. */
  loadMore: () => Promise<void>;
  /** Re-fetch from the first page (clears accumulated rows). */
  refresh: () => Promise<void>;
}

/**
 * Preserves the original error wording, including the contract's 400s
 * (INVALID_CURSOR / INVALID_LIMIT), which get a pagination-specific message.
 */
function toMessage(err: unknown): string {
  const e = (err ?? {}) as { code?: string; message?: string };
  if (e.code === 'INVALID_CURSOR' || e.code === 'INVALID_LIMIT') {
    return 'Could not load more history (invalid pagination). Try refreshing.';
  }
  return e.message || 'Failed to load task history.';
}

export function useTaskExecutionHistory(
  taskId: string | null | undefined,
  options: { pageSize?: number } = {}
): UseTaskExecutionHistoryReturn {
  const pageSize = options.pageSize ?? PAGE_SIZE;

  const query = useInfiniteQuery<ExecutionPage, Error>({
    // Outside the ['tasks'] prefix on purpose -- see queryKeys.taskExecutionPages. pageSize is part
    // of the key because a different page size is a different pagination sequence.
    queryKey: queryKeys.taskExecutionPages(taskId ?? '', pageSize),
    queryFn: ({ pageParam }) =>
      tasksApi.getExecutionsPageForTask(taskId as string, {
        limit: pageSize,
        cursor: pageParam as string | undefined,
      }),
    initialPageParam: undefined as string | undefined,
    // A null nextCursor means end of history; undefined is how TanStack represents "no further
    // pages", which is what drives hasNextPage.
    getNextPageParam: (last: ExecutionPage) => last.nextCursor ?? undefined,
    // Only fetch once a task is selected. TaskDetails passes null until the History tab is
    // opened, which is what keeps the request off tasks whose history is never viewed.
    enabled: !!taskId,
  });

  // isFetching is true for a BACKGROUND refetch too (every execution_updates flush invalidates
  // queryKeys.tasks.all, which reaches this query by prefix -- deliberately, so an open History tab
  // cannot go stale). Binding the Load More button to it meant that on a busy task the button was
  // disabled almost continuously while rows were arriving. isPending / isFetchingNextPage describe
  // the two states the UI actually cares about: nothing to show yet, and a page on the way.
  const { fetchNextPage, refetch, hasNextPage, isFetching, isFetchingNextPage, isPending } = query;

  // Tracks WHICH task has pending updates rather than a bare boolean, so switching tasks clears the
  // cue by DERIVATION. A boolean would need an effect to reset it, and an effect that calls
  // setState during render is exactly what react-hooks/set-state-in-effect forbids here.
  const [pendingFor, setPendingFor] = useState<string | null>(null);
  const hasPendingUpdates = !!taskId && pendingFor === taskId;

  // Optional on purpose: these rows render inside modals launched from list views, and a missing
  // WebSocketProvider must cost the cue, not throw (same rationale as useTaskManagement).
  const subscribe = useOptionalWebSocket()?.subscribe;

  useEffect(() => {
    if (!subscribe || !taskId) return;
    return subscribe('execution_updates', (data: any) => {
      // Every publisher sets taskId; a frame without one is not attributable, so ignore it.
      if (data?.taskId !== taskId) return;
      setPendingFor(taskId);
    });
  }, [subscribe, taskId]);

  const loadMore = useCallback(async () => {
    // Replaces the old cursorRef === null / loadingRef guards.
    //
    // Guards on isFetching (ANY fetch), not just isFetchingNextPage. query-core defaults
    // cancelRefetch to true, so calling fetchNextPage() while a background refetch is in flight
    // CANCELS that refetch and throws away the page it had already fetched -- the operator's click
    // would silently discard the newest executions and leave the tab on the pre-broadcast
    // snapshot, which is the very staleness this query stays live to avoid. Letting the click be a
    // no-op is not acceptable either, so the button is disabled with a visible "Refreshing..."
    // label instead (see TaskDetails) rather than looking clickable and doing nothing.
    if (!hasNextPage || isFetching) return;
    await fetchNextPage();
  }, [hasNextPage, isFetching, fetchNextPage]);

  const refresh = useCallback(async () => {
    // Clear the cue first: if the refetch fails, the operator should be able to try again, and a
    // cue that survives a successful refresh would be a lie.
    setPendingFor(null);
    await refetch();
  }, [refetch]);

  // A failed LATER page must not discard earlier pages. TanStack keeps data.pages intact on a
  // fetchNextPage error, which is what the old code hand-coded by only clearing rows in 'reset'
  // mode.
  const executions = query.data?.pages.flatMap(p => p.executions) ?? [];

  return {
    executions,
    // Initial load or an explicit page fetch -- NOT a background refetch.
    isLoading: isPending || isFetchingNextPage,
    isRefreshing: isFetching && !isPending && !isFetchingNextPage,
    error: query.error ? toMessage(query.error) : null,
    hasMore: !!hasNextPage,
    hasPendingUpdates,
    loadMore,
    refresh,
  };
}

export default useTaskExecutionHistory;
